/**
 * Leasing Reference Check — boundary contract schemas (DC-12/DC-17, BR-13).
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/leasing-reference-check.schemas.ts`
 * imports and re-exports these to register the routes, and
 * `apps/landlord/src/lib/leasing.api.ts` derives its `CreateReferenceCheckParams` /
 * `RefereeStatusView` / `GetReferenceCheckResponse` types from the same objects. See
 * packages/api-types/README.md.
 *
 * Covers:
 *   POST /landlord/api/leads/:id/reference-checks
 *   GET  /landlord/api/leads/:id/reference-checks/:referenceCheckId
 */
import { z } from 'zod';
const uuid = z.uuid();
const dateString = z.string(); // ISO 8601 string
// Must match RefereeStatus in @resida/database.
export const RefereeStatusSchema = z.enum([
    'pending',
    'contacted',
    'follow_up_sent',
    'responded',
    'exhausted',
]);
// ---------------------------------------------------------------------------
// Referee status view — content-minimized (never reply content, never referee context)
// ---------------------------------------------------------------------------
export const RefereeStatusViewSchema = z.object({
    id: uuid,
    email: z.string(),
    relationship: z.string(),
    status: RefereeStatusSchema,
    respondedAt: dateString.nullable(),
    exhaustedAt: dateString.nullable(),
});
// ---------------------------------------------------------------------------
// Create reference check — POST /landlord/api/leads/:id/reference-checks (request body)
// ---------------------------------------------------------------------------
export const CreateReferenceCheckBodySchema = z.object({
    applicantId: uuid,
    tenantNames: z.array(z.string().min(1)).min(1),
    landlordName: z.string().min(1),
    landlordContact: z.string().min(1),
    requestedInformation: z.string().min(1),
    referees: z
        .array(z.object({
        email: z.email(),
        relationship: z.string().min(1),
        context: z.string().optional(),
    }))
        .min(1),
});
// ---------------------------------------------------------------------------
// 409 Conflict — a create precondition is unmet. Owned here so the route's 409 response
// schema and the landlord UI's `err.code` comparison share one set of values.
// ---------------------------------------------------------------------------
export const ReferenceCheckConflictCodeSchema = z
    .enum(['CONSENT_INCOMPLETE', 'REFERENCE_AGENT_DISABLED'])
    .describe('CONSENT_INCOMPLETE — rental-history consent is not complete (BR-9); ' +
    'REFERENCE_AGENT_DISABLED — the reference agent is turned off for this landlord (BR-28)');
// ---------------------------------------------------------------------------
// Get reference check — GET /landlord/api/leads/:id/reference-checks/:referenceCheckId
// ---------------------------------------------------------------------------
export const GetReferenceCheckResponseSchema = z.object({
    referenceCheckId: uuid,
    /** The applicant the check was requested for — lets a deep link to a check find its applicant. */
    applicantId: uuid,
    status: z.enum(['open', 'closed']),
    closedAt: dateString.nullable(),
    summary: z.string().nullable(),
    referees: z.array(RefereeStatusViewSchema),
});
//# sourceMappingURL=reference-check.js.map